import { useState, useRef } from 'react';
import SEOHead from '../components/SEOHead';
import { imageUrl } from '../lib/images';

export default function JobPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const formRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    // Validate aggregate file limit
    if (files.length > 5) {
      setErrorMessage('Du kan højst uploade op til 5 filer.');
      return;
    }

    const totalBytes = files.reduce((acc, f) => acc + f.size, 0);
    const maxBytes = 20 * 1024 * 1024; // 20 MB
    if (totalBytes > maxBytes) {
      setErrorMessage('De valgte filer overskrider den maksimale samlede størrelse på 20 MB.');
      return;
    }

    // Validate single file size
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) {
        setErrorMessage(`Filen "${f.name}" fylder over 10 MB. Vælg venligst en mindre fil.`);
        return;
      }
    }

    setErrorMessage('');
    setSelectedFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);

    try {
      const formData = new FormData(formRef.current);

      // Verify honeypot is empty
      if (formData.get('website_url')) {
        // Silent bot discard
        setSuccess(true);
        setLoading(false);
        return;
      }

      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        body: formData, // FormData automatically sets multipart/form-data boundary
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Der opstod en fejl ved indsendelse af ansøgningen. Prøv venligst igen.');
      }

      setSuccess(true);
      formRef.current?.reset();
      setSelectedFiles([]);
    } catch (err) {
      setErrorMessage(err.message || 'Kunne ikke indsende ansøgningen. Tjek din internetforbindelse og prøv igen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Job & Bliv Behandler | Djurs Massage"
        description="Vil du være en del af Djurs Massage i Ryomgård? Vi søger engagerede behandlere og massører. Send din ansøgning direkte her."
        ogImage="https://assets.auxodev.dk/assets/5be3e3b2-e9a5-4c06-aea6-01d1054a5d99/djurs-massage-klinik-ryomgaard.jpg"
      />

      {/* Hero Header */}
      <section className="bg-[#041032] text-white py-14 sm:py-18 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#C9A770]/20 text-[#C9A770] text-xs font-bold uppercase tracking-wider rounded-full mb-4">
              <i className="fa-solid fa-briefcase text-xs" aria-hidden="true" />
              <span>Karriere & Samarbejde</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-serif leading-tight">
              Bliv en del af teamet hos Djurs Massage
            </h1>
            <p className="mt-4 text-base sm:text-lg text-white/95 leading-relaxed">
              Er du passioneret for sundhed, velvære og professionel kropsbehandling? Vi søger engagerede behandlere, der brænder for at gøre en mærkbar forskel for vores klienter.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-12 sm:py-16 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            
            {/* Left info column */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#1E3A5F] font-serif">
                  Hvorfor arbejde hos os?
                </h2>
                <p className="text-slate-700 text-sm sm:text-base mt-3 leading-relaxed">
                  Djurs Massage i Ryomgård er et anerkendt behandlerfællesskab med mere end 25 års erfaring, loyale klienter og en rolig, indbydende klinikstemning.
                </p>
              </div>

              {/* Value cards */}
              <div className="space-y-4">
                <div className="bg-white p-5 border border-[#E8E1D5] shadow-2xs flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#1E3A5F] text-[#C9A770] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-house-chimney-medical" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1E3A5F] text-base">Moderne klinikfaciliteter</h3>
                    <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">
                      Lyse, rolige behandlerrum udstyret med alt nødvendigt inventar, brikse, venteværelse og personalefaciliteter.
                    </p>
                  </div>
                </div>

                <div className="bg-white p-5 border border-[#E8E1D5] shadow-2xs flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#1E3A5F] text-[#C9A770] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-users" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1E3A5F] text-base">Tværfagligt fællesskab</h3>
                    <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">
                      Et inspirerende samarbejde mellem fysiurgiske massører, zoneterapeuter, kosmetologer og EMS-trænere.
                    </p>
                  </div>
                </div>

                <div className="bg-white p-5 border border-[#E8E1D5] shadow-2xs flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-[#1E3A5F] text-[#C9A770] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-calendar-days" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1E3A5F] text-base">Fleksibilitet & online booking</h3>
                    <p className="text-slate-600 text-xs sm:text-sm mt-1 leading-relaxed">
                      Tilrettelæg dine egne arbejdstider via vores integrerede Gecko online bookingsystem og store faste kundebase.
                    </p>
                  </div>
                </div>
              </div>

              {/* Hvem søger vi? */}
              <div className="bg-[#041032] text-white p-6 shadow-md border-l-4 border-[#C9A770]">
                <h3 className="text-lg font-bold font-serif text-[#C9A770]">
                  Hvem søger vi?
                </h3>
                <p className="text-xs text-white/90 mt-1 mb-3">
                  Vi søger selvstændige behandlere til vores hyggelige klinikfællesskab:
                </p>
                <ul className="space-y-2.5 text-xs sm:text-sm text-white">
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-[#C9A770] mt-1 shrink-0" aria-hidden="true" />
                    <span><strong>Massør:</strong> Gerne fysiurgisk og gerne RAB-godkendt (da behandlinger er momsfritaget)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-[#C9A770] mt-1 shrink-0" aria-hidden="true" />
                    <span><strong>Akupunktør:</strong> Gerne RAB-godkendt</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-[#C9A770] mt-1 shrink-0" aria-hidden="true" />
                    <span><strong>Kropsterapeut:</strong> Evt. kropsterapeut (gerne RAB-godkendt)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-[#C9A770] mt-1 shrink-0" aria-hidden="true" />
                    <span><strong>Uddannet kosmetolog eller fodplejer</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="fa-solid fa-check text-[#C9A770] mt-1 shrink-0" aria-hidden="true" />
                    <span>Behandlere interesseret i leje af kliniklokale</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right form column */}
            <div className="lg:col-span-7">
              <div className="bg-white p-6 sm:p-8 md:p-10 border border-[#E8E1D5] shadow-md">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-[#1E3A5F] font-serif">
                    Send din ansøgning
                  </h2>
                  <p className="text-slate-600 text-sm mt-1">
                    Udfyld formularen nedenfor og vedhæft dit CV og eventuel motiveret ansøgning. Vi behandler alle henvendelser fortroligt.
                  </p>
                </div>

                {success ? (
                  <div className="p-8 text-center bg-emerald-50 border border-emerald-200 rounded-lg space-y-4">
                    <div className="w-16 h-16 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center mx-auto text-2xl">
                      <i className="fa-solid fa-circle-check" aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-bold text-emerald-900 font-serif">
                      Tak for din ansøgning!
                    </h3>
                    <p className="text-sm text-emerald-800 leading-relaxed max-w-md mx-auto">
                      Vi har modtaget dine oplysninger og dokumenter. Vi gennemgår din ansøgning og kontakter dig snarest muligt for en uforpligtende samtale.
                    </p>
                    <button
                      type="button"
                      onClick={() => setSuccess(false)}
                      className="mt-4 px-6 py-2.5 bg-[#1E3A5F] text-white text-xs sm:text-sm font-semibold rounded-full hover:bg-black transition-colors cursor-pointer"
                    >
                      Send endnu en ansøgning
                    </button>
                  </div>
                ) : (
                  <form
                    ref={formRef}
                    onSubmit={handleSubmit}
                    className="space-y-5"
                    encType="multipart/form-data"
                  >
                    {/* Antispam honeypot: Mandatory sr-only input */}
                    <div className="sr-only absolute" aria-hidden="true">
                      <label htmlFor="website_url">Hjemmeside</label>
                      <input
                        id="website_url"
                        type="text"
                        name="website_url"
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    {/* Navn & E-mail */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="job-navn" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                          Fulde navn <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="job-navn"
                          name="navn"
                          type="text"
                          required
                          placeholder="F.eks. Mette Jensen"
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 focus:bg-white focus:border-[#1E3A5F] focus:ring-1 focus:ring-[#1E3A5F] text-sm text-slate-900 transition-colors"
                        />
                      </div>

                      <div>
                        <label htmlFor="job-email" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                          E-mailadresse <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="job-email"
                          name="email"
                          type="email"
                          required
                          placeholder="din@email.dk"
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 focus:bg-white focus:border-[#1E3A5F] focus:ring-1 focus:ring-[#1E3A5F] text-sm text-slate-900 transition-colors"
                        />
                      </div>
                    </div>

                    {/* Telefon & Fagområde */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="job-telefon" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                          Telefonnummer <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="job-telefon"
                          name="telefon"
                          type="tel"
                          required
                          placeholder="28 58 39 07"
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 focus:bg-white focus:border-[#1E3A5F] focus:ring-1 focus:ring-[#1E3A5F] text-sm text-slate-900 transition-colors"
                        />
                      </div>

                      <div>
                        <label htmlFor="job-fag" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                          Fagområde / Stilling <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="job-fag"
                          name="stilling"
                          required
                          className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 focus:bg-white focus:border-[#1E3A5F] focus:ring-1 focus:ring-[#1E3A5F] text-sm text-slate-900 transition-colors"
                        >
                          <option value="">Vælg dit fagområde...</option>
                          <option value="Massør (gerne fysiurgisk / RAB-godkendt)">Massør (gerne fysiurgisk / RAB-godkendt)</option>
                          <option value="Akupunktør (gerne RAB-godkendt)">Akupunktør (gerne RAB-godkendt)</option>
                          <option value="Kropsterapeut (gerne RAB-godkendt)">Kropsterapeut (gerne RAB-godkendt)</option>
                          <option value="Zoneterapeut (RAB-godkendt)">Zoneterapeut (RAB-godkendt)</option>
                          <option value="Uddannet kosmetolog / Fodplejer">Uddannet kosmetolog / Fodplejer</option>
                          <option value="Leje af kliniklokale">Interesseret i leje af kliniklokale</option>
                          <option value="Anden behandler / Uopfordret">Anden behandler / Uopfordret</option>
                        </select>
                      </div>
                    </div>

                    {/* Erfaring / Motiveret ansøgning */}
                    <div>
                      <label htmlFor="job-besked" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                        Kort om dig selv og din erfaring <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="job-besked"
                        name="besked"
                        rows={5}
                        required
                        placeholder="Fortæl kort om din uddannelsesmæssige baggrund, dine kompetencer og hvorfor du ønsker at arbejde hos Djurs Massage..."
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-300 focus:bg-white focus:border-[#1E3A5F] focus:ring-1 focus:ring-[#1E3A5F] text-sm text-slate-900 transition-colors"
                      />
                    </div>

                    {/* Filupload til CV og ansøgning */}
                    <div>
                      <label htmlFor="job-filer" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                        Vedhæft CV & eventuel ansøgning (PDF, Word, billeder)
                      </label>
                      <div className="border-2 border-dashed border-slate-300 p-4 sm:p-6 bg-slate-50/50 hover:bg-white transition-colors text-center">
                        <i className="fa-solid fa-cloud-arrow-up text-2xl text-[#C9A770] mb-2" aria-hidden="true" />
                        <p className="text-xs sm:text-sm text-slate-700 font-medium">
                          Vælg filer fra din enhed
                        </p>
                        <p className="text-[11px] text-slate-500 mt-1">
                          Tilladte formater: PDF, PNG, JPG, WEBP. Maks 5 filer, op til 20 MB i alt.
                        </p>
                        <input
                          id="job-filer"
                          name="ansogning_filer"
                          type="file"
                          multiple
                          accept=".pdf,.png,.jpg,.jpeg,.webp"
                          onChange={handleFileChange}
                          className="mt-3 block w-full text-xs text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-[#1E3A5F] file:text-white hover:file:bg-black file:cursor-pointer cursor-pointer"
                        />
                      </div>

                      {/* Vis valgte filer */}
                      {selectedFiles.length > 0 && (
                        <div className="mt-3 space-y-1.5">
                          <p className="text-xs font-semibold text-slate-700">Valgte filer:</p>
                          <ul className="text-xs text-slate-600 divide-y divide-slate-200 border border-slate-200 bg-white">
                            {selectedFiles.map((f, i) => (
                              <li key={i} className="py-1.5 px-3 flex items-center justify-between">
                                <span className="truncate max-w-xs">{f.name}</span>
                                <span className="text-slate-400">{(f.size / (1024 * 1024)).toFixed(2)} MB</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Fejlbesked */}
                    {errorMessage && (
                      <div className="p-3 bg-red-50 border-l-4 border-red-500 text-xs sm:text-sm text-red-700">
                        {errorMessage}
                      </div>
                    )}

                    {/* Submit Knap */}
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#C9A770] hover:bg-[#d8b884] text-[#041032] font-bold text-sm rounded-full shadow-sm hover:shadow-md transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                            <span>Indsender din ansøgning...</span>
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                            <span>Indsend jobansøgning</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}
